import { makeAutoObservable, runInAction, when } from "mobx";
import RootStore from "stores/RootStore";
import compareJobNumbers from "shared/common/utils/compareJobNumbers";

// Newest base number first, but a contract's suffixed halves keep their usual
// order beneath it -- reversing the whole comparison would put 3980V above
// 3980P and the bare 3980 below both.
const compareJobNumbersNewestFirst = (a: string, b: string): number => {
  const base = (value: string) => parseInt(value, 10);
  const left = base(a);
  const right = base(b);
  if (!Number.isNaN(left) && !Number.isNaN(right) && left !== right) {
    return right - left;
  }
  return compareJobNumbers(a, b);
};

// The Projects list's order: active first, then newest job number first. One
// definition, shared by the list and every select-project dropdown.
const compareProjectsForList = (
  a: { active: boolean; jobNumber: string },
  b: { active: boolean; jobNumber: string }
): number =>
  Number(b.active) - Number(a.active) ||
  compareJobNumbersNewestFirst(a.jobNumber, b.jobNumber);
import { mmErrorMessage } from "./mmErrorMessage";
import {
  ENTITY_COLOR_COUNT,
  getEntityColorByIndex,
} from "views/millermize/entityColors";

// The API's DATE columns come back as full ISO datetimes
// (e.g. "2026-06-24T00:00:00.000Z"). The backend validates writes as
// `format: 'date'` (yyyy-MM-dd), so a re-sent datetime fails validation on
// patch/create. Normalize to a plain date string everywhere we read/write
// these. Do NOT apply to createdAt/updatedAt (those are datetimes).
const toDateOnly = (v: unknown): string =>
  v == null ? "" : String(v).split("T")[0];

// Today in LOCAL time. toISOString() is UTC, which in US Central rolls over to
// tomorrow in the early evening -- a move made at 7 pm logged the next day.
const localToday = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

// ============================================================================
// TYPES
// ============================================================================

export type ProductType = "precast" | "vdt";
// Product filter for the project list — "paired" = "VDT + precast"
// combination jobs (anything with a non-null pairedProjectId).
export type ProductTypeFilter = ProductType | "all" | "paired";
export type TaxStatus = "taxed" | "exempt" | "?";
export type DetailingStatus =
  | "new_not_assigned"
  | "sent_to_detailers"
  | "out_for_approval"
  | "revise_resubmit"
  | "approved"
  // Part of the tickets are in, so production can start (Creede, 2026-10-16):
  // "when we get the first half of the tickets, but maybe not all… we can
  // drop it down in that section below." Sits under Approved on the board.
  | "approved_partial"
  | "plant"
  | "on_hold";

// A job number is digits plus an optional letter suffix: the two halves of one
// contract are "3980V" (VDT) and "3980P" (precast). Same shape the API accepts
// (mm-projects.schema.ts) — keep the two in step.
export const JOB_NUMBER_PATTERN = /^\d+[A-Za-z]{0,2}$/;

// Creede and Dutch, 2026-08-19: "estimated rate and actual rate should both be
// tracked and shown throughout, with Creede/Dutsch being able to choose which
// rate they decide to list."
export type BillingRateBasis = "estimate" | "actual";

export type AdministrationTab = "info" | "de" | "billing";

export interface MMProject {
  id: string;
  // String, not a number: see JOB_NUMBER_PATTERN. Suffixes are stored
  // upper-cased (the DB collation is case-insensitive, so "3980v" and "3980V"
  // would be two rows every query treats as one).
  jobNumber: string;
  jobName: string;
  jobSiteAddress: string;
  jobSiteCity: string;
  jobSiteState: string;
  jobSiteZip: string;

  // Customer link
  companyId: string;
  billingContactId?: string | null;
  projectManagerId?: string | null;
  jobSiteContactId?: string | null;

  // Finance
  productType: ProductType;
  // The sibling job of a VDT/precast pair (item 12). The two stay separate
  // everywhere except shipping, where they must leave together.
  // null/undefined = unpaired.
  pairedProjectId?: string | null;
  contractAmount: number;
  taxStatus: TaxStatus;

  // Billing calculation
  totalCubicFeet: number;
  // The ticketed figure. NULL until tickets are finalled — 0 is a real
  // actual (tickets entered, nothing produced), so it cannot mean "unset".
  actualCubicFeet: number | null;
  // What the batch plant mixes. Free text — 30-40% of jobs are custom
  // colours. Deliberately NOT added to updateProject's optionalStringFields,
  // which delete blank values from the patch and would make a colour
  // impossible to clear once set; blank is sent as null instead.
  concreteColor: string | null;
  /**
   * The job's mix design, as a blob URL (item 18). Creede, 2026-08-26: "Even if
   * we make it in Excel, we'll export it as a PDF." null = none uploaded.
   */
  mixDesignFileUrl: string | null;
  /**
   * The colour this job wears on the schedule, held from first appearance until
   * the job completes (item 6). Creede: "On this week here, this 3928 is
   * purple. When I go to the next week, it's blue."
   *
   * null = not assigned yet, which is every row before this shipped; the grid
   * falls back to its old rank-based derivation while it is null, so nothing
   * regresses before jobs are coloured. Clearing it back to null on completion
   * is how a shade returns to the pool of sixteen.
   *
   * Deliberately NOT `concreteColor`, which is the physical colour of the
   * concrete and is headed for a printed day sheet.
   */
  scheduleColor: string | null;
  // Which cubic-foot figure billingRatePerCuFt divides by. Both rates are
  // derived and displayed; this is the choice Creede and Dutch make.
  billingRateBasis: BillingRateBasis;
  designEngineeringAllocation: number;
  mockupAllocation: number;
  // The estimator's own totals for the job's forms (10-27 item 1). Optional so
  // a save that never touched them doesn't name columns an unmigrated database
  // lacks; null = not estimated.
  estimatorForms?: number | null;
  estimatorFormHours?: number | null;
  billingRatePerCuFt: number;

  // Detailing pipeline status
  detailingStatus: DetailingStatus;

  // Detailer assignments
  engineeringDetailer?: string;
  erectionDetailer?: string;
  ticketDetailer?: string;

  // Dates
  dateBooked?: string;
  sentToDetailersDate?: string;
  submittalDate?: string;
  approvalDate?: string;
  holdDate?: string;

  /**
   * Is the job being worked on (Creede, 2026-09-23)? Replaces his folder habit
   * of typing "active" in front of a job's name so the OS sorts it first.
   * Active jobs sort to the top of the list; inactive ones stay listed below --
   * hiding them waits on Dutch saying whether billing still needs them.
   *
   * The column has existed since the table was created (BIT NOT NULL DEFAULT
   * 1), so every job starts active and no migration was needed.
   */
  active: boolean;

  // Status flags
  hasShopAndField: boolean;
  hasProductionTickets: boolean;
  // "The ticket set is COMPLETE" — distinct from hasProductionTickets, which
  // only means tickets have started arriving. Tickets land in batches a week
  // apart, so mid-entry actuals are legitimately low and the ESTIMATE has to
  // govern the invoice until Creede says otherwise. Deliberately NOT an input
  // to `readyForProduction`.
  productionTicketsFinal: boolean;
  hasRequisitions: boolean;
  hasErectionRequisitions: boolean;
  hasShopAndFieldFinal: boolean;
  readyForProduction: boolean;
}

// ============================================================================
// HELPER
// ============================================================================

// In the board's order, which every status dropdown follows: Revise &
// Resubmit beneath Sent to Detailers -- "if it gets revised and resubmitted,
// it actually is still back at the detailers" -- and Approved – Partial
// Tickets beneath Approved (Creede, 2026-10-16).
export const DETAILING_STATUS_LABELS: Record<DetailingStatus, string> = {
  new_not_assigned: "New - Not Assigned",
  sent_to_detailers: "Sent to Detailers",
  revise_resubmit: "Revise & Resubmit",
  out_for_approval: "Out for Approval",
  approved: "Approved",
  approved_partial: "Approved – Partial Tickets",
  plant: "Plant – All Tickets",
  on_hold: "On Hold",
};

export const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  precast: "Precast",
  vdt: "VDT",
};

export const PRODUCT_TYPE_COLORS: Record<ProductType, string> = {
  precast: "#4caf50", // Green
  vdt: "#2196f3", // Blue
};

/**
 * One concrete colour a job pours, with its own mix design.
 *
 * Creede, 2026-09-15, on a job that arrived that week in limestone, white and a
 * special black: "I could click this and then do the same way we do forms, a
 * plus and add another color. I mean, I could just be able to upload multiple
 * mix designs."
 *
 * `MMProject.concreteColor` and `.mixDesignFileUrl` are still populated and
 * still read by the printed day sheet. These rows are the truth going forward;
 * the single columns are the compatibility layer until the sheet moves over.
 */
export interface MMProjectColor {
  id: string;
  projectId: string;
  color: string;
  mixDesignFileUrl: string | null;
  /** The plant's Mix # for this colour (2026-10-16 item 26); null = not set. */
  mixNumber: string | null;
  sortOrder: number;
}

/**
 * One anchor / material requisition PDF on a job (09-23 item 14). Creede: a
 * tab where he can "drop this PDF in and I can click on and view and print it."
 */
export type AnchorMaterialType = "erection" | "plant" | "field";
export interface MMProjectAnchor {
  id: string;
  projectId: string;
  materialType: AnchorMaterialType;
  fileUrl: string;
  fileName: string;
  /** yyyy-MM-dd, the date printed on the requisition; null when unknown. */
  documentDate: string | null;
}

/**
 * What a requisition's FILE NAME says it is. Creede names them the way the
 * requisition reads -- "Hoover NCFI ... - Erection Material 06-30-26.pdf" -- so
 * the name is a better first guess than the PDF's text layer, whose reading
 * order scatters "Material Type:" away from its value. Only a guess: both are
 * editable on the row.
 */
export const guessAnchorDetails = (
  fileName: string
): { materialType: AnchorMaterialType; documentDate: string | null } => {
  const materialType: AnchorMaterialType = /plant/i.test(fileName)
    ? "plant"
    : /field/i.test(fileName)
      ? "field"
      : "erection";
  const match = fileName.match(/(\d{1,2})[-_.](\d{1,2})[-_.](\d{2}|\d{4})(?!\d)/);
  let documentDate: string | null = null;
  if (match) {
    const [, m, d, y] = match;
    const year = y.length === 2 ? `20${y}` : y;
    const month = Number(m);
    const day = Number(d);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      documentDate = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
  }
  return { materialType, documentDate };
};

/**
 * A contact on a job in a role (10-07 item 10). Creede: "I've got three billing
 * contacts on this job." The first of each role is also written to the job's
 * single column, which the load list and load form still read.
 */
export type ProjectContactRole = "billing" | "project_manager" | "job_site";
export interface MMProjectContact {
  id: string;
  projectId: string;
  contactId: string;
  role: ProjectContactRole;
  sortOrder: number;
}
/**
 * One move through the detailing pipeline (10-16 item 6). Creede: "So we know
 * when something's booked, how long it's been at the detailers, how long it's
 * been out for reapproval… be a log." Written by updateProject on every status
 * change, whichever screen made it.
 */
export interface MMProjectStageEvent {
  id: string;
  projectId: string;
  fromStatus: string | null;
  toStatus: string;
  occurredOn: string;
  createdBy: string | null;
  createdAt: string;
}

// The single date column each status has stamped since the pipeline began.
// Still written (other screens read them), and the fallback for a job's
// "since" date when the log has nothing.
export const STATUS_DATE_FIELD: Partial<
  Record<DetailingStatus, "dateBooked" | "sentToDetailersDate" | "submittalDate" | "approvalDate" | "holdDate">
> = {
  new_not_assigned: "dateBooked",
  sent_to_detailers: "sentToDetailersDate",
  out_for_approval: "submittalDate",
  approved: "approvalDate",
  approved_partial: "approvalDate",
  on_hold: "holdDate",
};

const ROLE_COLUMN: Record<ProjectContactRole, "billingContactId" | "projectManagerId" | "jobSiteContactId"> = {
  billing: "billingContactId",
  project_manager: "projectManagerId",
  job_site: "jobSiteContactId",
};

// The API's row, as it arrives.
interface StageEventRow {
  stageEventId: string;
  projectId: string;
  fromStatus?: string | null;
  toStatus: string;
  occurredOn: string;
  createdBy?: string | null;
  createdAt?: string;
}

const rowsOf = <T,>(result: unknown): T[] =>
  Array.isArray(result) ? (result as T[]) : ((result as { data?: T[] })?.data ?? []);

const toStageEvent = (e: StageEventRow): MMProjectStageEvent => ({
  id: e.stageEventId,
  projectId: e.projectId,
  fromStatus: e.fromStatus ?? null,
  toStatus: e.toStatus,
  occurredOn: toDateOnly(e.occurredOn),
  createdBy: e.createdBy ?? null,
  createdAt: e.createdAt ? String(e.createdAt) : "",
});

const toAnchor = (a: any): MMProjectAnchor => ({
  id: a.projectAnchorId,
  projectId: a.projectId,
  // "field" since 10-16 item 23; anything unknown still reads as erection.
  materialType: a.materialType === "plant" || a.materialType === "field" ? a.materialType : "erection",
  fileUrl: a.fileUrl,
  fileName: a.fileName,
  documentDate: a.documentDate ? String(a.documentDate).slice(0, 10) : null,
});

// ============================================================================
// STORE
// ============================================================================

class MMProjectStore {
  projects: MMProject[] = [];
  projectColors: MMProjectColor[] = [];
  projectAnchors: MMProjectAnchor[] = [];
  projectContacts: MMProjectContact[] = [];
  projectStageEvents: MMProjectStageEvent[] = [];
  selectedProjectId: string | null = null;
  searchTerm: string = "";
  filterByStatus: DetailingStatus | "all" = "all";
  filterByProductType: ProductTypeFilter = "all";
  // On by default, so nothing disappears on release. Dutsch, via Creede
  // (2026-09-29): "inactive jobs are still accessed regularly. We can hide them,
  // but it should be implemented as a simple filter that users can easily
  // toggle on and off."
  showInactive: boolean = true;
  // The Administration page's job and tab (2026-10-16). Its own, not
  // Production's: the office and the floor look at different jobs.
  administrationProjectId: string | null = null;
  administrationTab: AdministrationTab = "info";
  isLoading: boolean = false;
  error: string | null = null;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeAutoObservable<MMProjectStore, "scheduleColorQueue">(this, {
      rootStore: false,
      scheduleColorQueue: false,
    });
    this.rootStore = rootStore;

    // Auto-load when authenticated
    when(
      () => !!(this.rootStore.authStore as any)?.isSignedIn,
      () => this.loadData()
    );
  }

  // ========================================
  // SERVICE HELPER
  // ========================================

  private get service() {
    return this.rootStore.app.service("mm-projects");
  }

  private get colorsService() {
    return this.rootStore.app.service("mm-project-colors");
  }

  private get contactsService() {
    return this.rootStore.app.service("mm-project-contacts");
  }

  private get stageEventsService() {
    return this.rootStore.app.service("mm-project-stage-events");
  }

  private get anchorsService() {
    return this.rootStore.app.service("mm-project-anchors");
  }

  // ========================================
  // DATA LOADING
  // ========================================

  loadData = async () => {
    if (this.isLoading) return;

    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      // One round trip each, in parallel: the colours are a small child table
      // and every job's picker needs them before the first dialog opens.
      const [result, colorsResult, anchorsResult, contactsResult, eventsResult] = await Promise.all([
        this.service.find({ query: { $limit: 1000 } }),
        this.colorsService
          .find({ query: { $limit: 2000, active: true } })
          .catch(() => ({ data: [] })),
        // Caught like the colours: an API deployed before its migration ran
        // must not take the whole project list down with it.
        this.anchorsService
          .find({ query: { $limit: 2000, active: true } })
          .catch(() => ({ data: [] })),
        // Same tolerance: an API deployed before its migration ran.
        this.contactsService
          .find({ query: { $limit: 5000, active: true } })
          .catch(() => ({ data: [] })),
        // Same tolerance again (the 10-20 migration).
        this.stageEventsService
          .find({ query: { $limit: 20000, active: true } })
          .catch(() => ({ data: [] })),
      ]);
      const eventRows = rowsOf<StageEventRow>(eventsResult);
      const contactRows: any[] = Array.isArray(contactsResult)
        ? contactsResult
        : (contactsResult as any).data || [];
      const anchorRows: any[] = Array.isArray(anchorsResult)
        ? anchorsResult
        : (anchorsResult as any).data || [];
      const data: any[] = Array.isArray(result)
        ? result
        : (result as any).data || [];
      const colorRows: any[] = Array.isArray(colorsResult)
        ? colorsResult
        : (colorsResult as any).data || [];

      const projects: MMProject[] = data.map((p: any) => ({
        id: p.projectId,
        // An API that predates the NVARCHAR migration still sends a number.
        jobNumber: p.jobNumber == null ? "" : String(p.jobNumber),
        jobName: p.jobName,
        jobSiteAddress: p.jobSiteAddress || "",
        jobSiteCity: p.jobSiteCity || "",
        jobSiteState: p.jobSiteState || "",
        jobSiteZip: p.jobSiteZip || "",
        companyId: p.companyId,
        billingContactId: p.billingContactId,
        projectManagerId: p.projectManagerId,
        jobSiteContactId: p.jobSiteContactId,
        productType: p.productType || "precast",
        // ?? (not ||) — null must stay null (= unpaired), and a real uuid
        // must never be coerced.
        pairedProjectId: p.pairedProjectId ?? null,
        contractAmount: Number(p.contractAmount) || 0,
        taxStatus: p.taxStatus || "exempt",
        // Only an explicit false is inactive: a missing value is the column's
        // own default.
        active: p.active !== false,
        totalCubicFeet: Number(p.totalCubicFeet) || 0,
        // Deliberately not `|| 0`: that would turn a genuine 0 and a missing
        // value into the same thing, which is the distinction this field exists
        // to keep.
        actualCubicFeet:
          p.actualCubicFeet == null ? null : Number(p.actualCubicFeet),
        concreteColor: p.concreteColor ?? null,
        mixDesignFileUrl: p.mixDesignFileUrl ?? null,
        scheduleColor: p.scheduleColor ?? null,
        billingRateBasis:
          p.billingRateBasis === "actual" ? "actual" : "estimate",
        designEngineeringAllocation: Number(p.designEngineeringAllocation) || 0,
        mockupAllocation: Number(p.mockupAllocation) || 0,
        // `== null`, not `|| 0`: an estimate of 0 is not "not estimated".
        estimatorForms: p.estimatorForms == null ? null : Number(p.estimatorForms),
        estimatorFormHours: p.estimatorFormHours == null ? null : Number(p.estimatorFormHours),
        billingRatePerCuFt: Number(p.billingRatePerCuFt) || 0,
        detailingStatus: p.detailingStatus || "new_not_assigned",
        engineeringDetailer: p.engineeringDetailer,
        erectionDetailer: p.erectionDetailer,
        ticketDetailer: p.ticketDetailer,
        dateBooked: toDateOnly(p.dateBooked),
        sentToDetailersDate: toDateOnly(p.sentToDetailersDate),
        submittalDate: toDateOnly(p.submittalDate),
        approvalDate: toDateOnly(p.approvalDate),
        holdDate: toDateOnly(p.holdDate),
        hasShopAndField: !!p.hasShopAndField,
        hasProductionTickets: !!p.hasProductionTickets,
        productionTicketsFinal: !!p.productionTicketsFinal,
        hasRequisitions: !!p.hasRequisitions,
        hasErectionRequisitions: !!p.hasErectionRequisitions,
        hasShopAndFieldFinal: !!p.hasShopAndFieldFinal,
        readyForProduction: !!p.readyForProduction,
      }));

      runInAction(() => {
        this.projects = projects;
        this.projectColors = colorRows.map((c: any) => ({
          id: c.projectColorId,
          projectId: c.projectId,
          color: c.color,
          mixDesignFileUrl: c.mixDesignFileUrl ?? null,
          mixNumber: c.mixNumber ?? null,
          sortOrder: Number(c.sortOrder) || 0,
        }));
        this.projectAnchors = anchorRows.map(toAnchor);
        this.projectContacts = contactRows.map((c: any) => ({
          id: c.projectContactId,
          projectId: c.projectId,
          contactId: c.contactId,
          role: c.role,
          sortOrder: Number(c.sortOrder) || 0,
        }));
        this.projectStageEvents = eventRows.map(toStageEvent);
        this.isLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.isLoading = false;
        this.error =
          error instanceof Error ? error.message : "Failed to load projects";
      });
      console.error("Failed to load projects:", error);
    }
  };

  // ========================================
  // COMPUTED
  // ========================================

  get filteredProjects(): MMProject[] {
    let result = this.projects;

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      result = result.filter(
        (p) =>
          p.jobName.toLowerCase().includes(term) ||
          p.jobNumber.toLowerCase().includes(term) ||
          p.jobSiteCity.toLowerCase().includes(term)
      );
    }

    if (!this.showInactive) {
      result = result.filter((p) => p.active);
    }

    if (this.filterByStatus !== "all") {
      result = result.filter((p) => p.detailingStatus === this.filterByStatus);
    }

    if (this.filterByProductType === "paired") {
      // "VDT + precast" combination jobs — anything with a pairing.
      result = result.filter((p) => p.pairedProjectId != null);
    } else if (this.filterByProductType !== "all") {
      result = result.filter((p) => p.productType === this.filterByProductType);
    }

    // Copy before sorting — with no filter active `result` IS the observable
    // array. Creede, 2026-09-23: active jobs first, then NEWEST first -- "this
    // job in a year's time being at the top is going to... just be devoid and
    // taking up space." A contract's halves still sit together, in the same
    // order as before (3980P above 3980V): only the job numbers run downward.
    return [...result].sort(compareProjectsForList);
  }

  // Every project, in the Projects list's order, for the select-project
  // dropdowns. Creede, 2026-10-16: "these select project dropdown menus, I
  // would like to be sorted the same way projects is." The list's search and
  // filters do NOT apply -- a dropdown searches by itself.
  setAdministrationProjectId = (id: string | null) => {
    this.administrationProjectId = id;
  };

  setAdministrationTab = (tab: AdministrationTab) => {
    this.administrationTab = tab;
  };

  get projectsForPicker(): MMProject[] {
    return [...this.projects].sort(compareProjectsForList);
  }

  get selectedProject(): MMProject | null {
    if (!this.selectedProjectId) return null;
    return this.projects.find((p) => p.id === this.selectedProjectId) || null;
  }

  // The next unused base number, suffix-less: a new job starts as "3981", and
  // the operator adds V/P by hand when the contract is split. Derived from the
  // numeric PREFIX only (parseInt stops at the suffix) — reading the whole
  // value as a number would make one suffixed job poison the maximum with NaN.
  get nextJobNumber(): string {
    const highest = this.projects.reduce((max, p) => {
      const prefix = parseInt(p.jobNumber, 10);
      return Number.isNaN(prefix) ? max : Math.max(max, prefix);
    }, 0);
    return String(highest > 0 ? highest + 1 : 3900);
  }

  /** The jobs the Detailing Pipeline shows: active ones (2026-10-23, item 35). */
  get pipelineProjects(): MMProject[] {
    return this.projects.filter((p) => p.active);
  }

  get projectsByStatus(): Record<DetailingStatus, MMProject[]> {
    const result: Record<DetailingStatus, MMProject[]> = {
      new_not_assigned: [],
      sent_to_detailers: [],
      revise_resubmit: [],
      out_for_approval: [],
      approved: [],
      approved_partial: [],
      plant: [],
      on_hold: [],
    };

    // Active jobs only (Creede, email 2026-10-23: "having only active jobs
    // displayed on the Detailing Pipeline"). The dashboard's Pipeline card
    // reads this too, so it follows.
    this.pipelineProjects.forEach((p) => {
      // A status this build doesn't know (a newer API) lands in New rather
      // than throwing the whole board away.
      (result[p.detailingStatus] ?? result.new_not_assigned).push(p);
    });

    // Oldest first, by the day each job entered its lane (Creede, 2026-10-16:
    // "our oldest one's going to be at the top"). Undated jobs go last.
    const since = new Map(this.projects.map((p) => [p.id, this.enteredStatusOn(p) ?? ""]));
    for (const lane of Object.values(result)) {
      lane.sort((a, b) => {
        const left = since.get(a.id) || "9999";
        const right = since.get(b.id) || "9999";
        return left.localeCompare(right) || compareProjectsForList(a, b);
      });
    }

    return result;
  }

  // Item 6 — give a job a colour and let it keep it.
  //
  // Creede diagnosed the bug himself: colours are assigned by rank among the
  // jobs visible in the week being rendered, so the rank — and the colour —
  // moves whenever the set of jobs on screen changes. Ranking what is on screen
  // is what guarantees a week's jobs are mutually distinct, which a hash cannot,
  // so the fix is to STORE the choice rather than derive it differently.
  //
  // Assigns the LEAST-USED shade among jobs currently holding one. With sixteen
  // shades and more than sixteen live jobs, two jobs share a colour rather than
  // one going blank — degraded, but still readable, which is the point:
  // "even without reading this, I know my purple job's here."
  //
  // Idempotent and safe to call on every render pass: a job that already has a
  // colour returns it without a write.
  //
  // ONE AT A TIME. The schedule asks for every uncoloured job in the week at
  // once, and a choice only becomes visible to the next one after its write
  // returns -- so six never-coloured jobs each saw an empty pool and all six
  // were saved as the first shade, which is worse than the rank fallback this
  // replaced. Queued, each pick sees the picks before it.
  private scheduleColorQueue: Promise<unknown> = Promise.resolve();

  ensureScheduleColor = (projectId: string): Promise<string | null> => {
    const pick = this.scheduleColorQueue.then(() =>
      this.pickScheduleColor(projectId)
    );
    this.scheduleColorQueue = pick.catch(() => null);
    return pick;
  };

  private pickScheduleColor = async (
    projectId: string
  ): Promise<string | null> => {
    const project = this.getProjectById(projectId);
    if (!project) return null;
    if (project.scheduleColor) return project.scheduleColor;

    const inUse = new Map<string, number>();
    for (let index = 0; index < ENTITY_COLOR_COUNT; index += 1) {
      inUse.set(getEntityColorByIndex(index), 0);
    }
    for (const other of this.projects) {
      if (!other.scheduleColor) continue;
      // A shade outside the palette (hand-set, or a palette that has since
      // changed) still counts as taken, so we do not hand it out twice.
      inUse.set(other.scheduleColor, (inUse.get(other.scheduleColor) ?? 0) + 1);
    }

    let chosen = getEntityColorByIndex(0);
    let lowest = Number.POSITIVE_INFINITY;
    // Walks the palette in order, so ties resolve to the earliest shade and the
    // assignment is reproducible.
    for (let index = 0; index < ENTITY_COLOR_COUNT; index += 1) {
      const color = getEntityColorByIndex(index);
      const count = inUse.get(color) ?? 0;
      if (count < lowest) {
        lowest = count;
        chosen = color;
      }
    }

    const saved = await this.updateProject(projectId, {
      scheduleColor: chosen,
    });
    // A failed write must not pretend the job is coloured — the grid falls back
    // to its rank derivation, which is what it did before this existed.
    return saved ? chosen : null;
  };

  // Hand a colour back to the pool. Called when a job finishes, which is the
  // half of Creede's convention that keeps sixteen shades sufficient: "pick a
  // colour per job, hold it until the job completes, then return it".
  releaseScheduleColor = async (projectId: string): Promise<void> => {
    const project = this.getProjectById(projectId);
    if (!project?.scheduleColor) return;
    await this.updateProject(projectId, { scheduleColor: null });
  };

  // Item 18 — upload a job's mix design and return its blob URL.
  //
  // The three-step flow the rest of the app already uses (see
  // views/fieldReporting/Dashboard/FieldActivityIssues.Store.ts): ask the
  // backend for a SAS URL, PUT the raw File straight to Azure, then tell the
  // backend the upload finished.
  //
  // The URL is RETURNED, not written here. The caller stores it on the project
  // as part of the ordinary save, because the blob service keeps its records in
  // an in-memory Map that does not survive a restart -- the project row is the
  // only durable record that this file belongs to this job.
  //
  // Returns null on any failure, having already told the user. Callers must
  // treat null as "nothing was uploaded" and leave the existing value alone.
  uploadMixDesign = (projectId: string, file: File): Promise<string | null> =>
    this.uploadProjectFile(projectId, file, "mm-mix-design", "the mix design");

  // The same upload, for any PDF that belongs to a job -- a mix design, or an
  // anchor list (09-23 item 14).
  private uploadProjectFile = async (
    projectId: string,
    file: File,
    entityType: string,
    label: string
  ): Promise<string | null> => {
    const blobStore = (this.rootStore as any).azureBlobStorageStore;
    if (!blobStore) {
      this.rootStore.notificationStore?.addNotification({
        message: "File uploads are unavailable right now.",
        options: { variant: "error" },
      });
      return null;
    }

    try {
      const record = await blobStore.create({
        requestBlobUrl: true,
        fileName: file.name || `${entityType}.pdf`,
        contentType: file.type || "application/pdf",
        fileSize: file.size,
        metadata: {
          uploadedBy: this.rootStore.authStore?.user?.email || "unknown",
          entityType,
          projectId,
        },
        tags: [entityType],
      });

      if (!record?.uploadUrl) {
        throw new Error("No upload URL was issued");
      }

      const response = await fetch(record.uploadUrl, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": file.type || "application/pdf",
        },
        body: file,
      });
      if (!response.ok) {
        throw new Error(
          `Upload failed with status ${response.status}: ${response.statusText}`
        );
      }

      await blobStore.patch(record.id, {
        uploadComplete: true,
        actualFileSize: file.size,
      });

      return record.blobUrl ?? null;
    } catch (error) {
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, `Failed to upload ${label}`),
        options: { variant: "error" },
      });
      console.error(`Upload of ${label} failed:`, error);
      return null;
    }
  };

  // ========================================
  // JOB CONTACTS BY ROLE (10-07 item 10)
  // ========================================

  /**
   * A job's contacts in a role, first first. Falls back to the job's single
   * column when there are no rows -- a job saved by an API that predates the
   * table, or one not reloaded since.
   */
  contactIdsFor = (projectId: string, role: ProjectContactRole): string[] => {
    const rows = this.projectContacts
      .filter((c) => c.projectId === projectId && c.role === role)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    if (rows.length > 0) return rows.map((c) => c.contactId);
    const single = this.getProjectById(projectId)?.[ROLE_COLUMN[role]];
    return single ? [single] : [];
  };

  /**
   * Replace a job's contacts in a role with `contactIds`, in that order. Rows
   * are added, removed and re-ordered individually; the role's single column is
   * set to the first (or cleared) so everything still reading it agrees.
   */
  setProjectContacts = async (
    projectId: string,
    role: ProjectContactRole,
    contactIds: string[]
  ): Promise<boolean> => {
    const wanted = [...new Set(contactIds.filter(Boolean))];
    const existing = this.projectContacts.filter(
      (c) => c.projectId === projectId && c.role === role
    );
    try {
      for (const row of existing) {
        if (!wanted.includes(row.contactId)) {
          await this.contactsService.remove(row.id);
          runInAction(() => {
            this.projectContacts = this.projectContacts.filter((c) => c.id !== row.id);
          });
        }
      }
      for (const [sortOrder, contactId] of wanted.entries()) {
        const row = this.projectContacts.find(
          (c) => c.projectId === projectId && c.role === role && c.contactId === contactId
        );
        if (!row) {
          const created: any = await this.contactsService.create({ projectId, contactId, role, sortOrder });
          runInAction(() => {
            this.projectContacts.push({
              id: created.projectContactId,
              projectId,
              contactId,
              role,
              sortOrder,
            });
          });
        } else if (row.sortOrder !== sortOrder) {
          await this.contactsService.patch(row.id, { sortOrder });
          runInAction(() => {
            row.sortOrder = sortOrder;
          });
        }
      }
    } catch (error) {
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to save the job's contacts"),
        options: { variant: "error" },
      });
      return false;
    }
    const first = wanted[0] ?? null;
    const column = ROLE_COLUMN[role];
    if ((this.getProjectById(projectId)?.[column] ?? null) !== first) {
      return this.updateProject(projectId, { [column]: first } as Partial<MMProject>);
    }
    return true;
  };

  // ========================================
  // ANCHOR LISTS (09-23 item 14)
  // ========================================

  /** A job's material lists: erection, field, plant, newest document first. */
  anchorsForProject = (projectId: string): MMProjectAnchor[] =>
    this.projectAnchors
      .filter((a) => a.projectId === projectId)
      .slice()
      .sort(
        (a, b) =>
          a.materialType.localeCompare(b.materialType) ||
          (b.documentDate ?? "").localeCompare(a.documentDate ?? "") ||
          a.fileName.localeCompare(b.fileName)
      );

  /** Upload a requisition PDF and file it on the job. Null on failure. */
  addProjectAnchor = async (
    projectId: string,
    file: File
  ): Promise<MMProjectAnchor | null> => {
    const fileUrl = await this.uploadProjectFile(
      projectId,
      file,
      "mm-anchor-list",
      "the anchor list"
    );
    if (!fileUrl) return null;
    try {
      const created: any = await this.anchorsService.create({
        projectId,
        fileUrl,
        fileName: file.name || "anchor-list.pdf",
        ...guessAnchorDetails(file.name || ""),
      });
      const row = toAnchor(created);
      runInAction(() => {
        this.projectAnchors.push(row);
      });
      return row;
    } catch (error) {
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to add the anchor list"),
        options: { variant: "error" },
      });
      return null;
    }
  };

  updateProjectAnchor = async (
    id: string,
    data: Partial<Pick<MMProjectAnchor, "materialType" | "documentDate">>
  ): Promise<boolean> => {
    try {
      const patched: any = await this.anchorsService.patch(id, data);
      runInAction(() => {
        const index = this.projectAnchors.findIndex((a) => a.id === id);
        if (index !== -1) this.projectAnchors[index] = toAnchor(patched);
      });
      return true;
    } catch (error) {
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to update the anchor list"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  deleteProjectAnchor = async (id: string): Promise<boolean> => {
    try {
      await this.anchorsService.remove(id);
      runInAction(() => {
        this.projectAnchors = this.projectAnchors.filter((a) => a.id !== id);
      });
      return true;
    } catch (error) {
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to remove the anchor list"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  // ========================================
  // JOB COLOURS (2026-09-15)
  // ========================================

  /**
   * The colours this job pours, in the order he added them.
   *
   * Falls back to the job's single `concreteColor` when the child table has no
   * rows for it, so a job created before this shipped -- or created by a path
   * that has not been moved over yet -- still offers the colour it has. The
   * fallback row carries an empty id, which is how callers tell it apart from a
   * real row they could edit.
   */
  colorsForProject = (projectId: string): MMProjectColor[] => {
    const rows = this.projectColors
      .filter((c) => c.projectId === projectId)
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder || a.color.localeCompare(b.color));
    if (rows.length > 0) return rows;

    const project = this.getProjectById(projectId);
    const legacy = project?.concreteColor?.trim();
    return legacy
      ? [
          {
            id: "",
            projectId,
            color: legacy,
            mixDesignFileUrl: project?.mixDesignFileUrl ?? null,
            mixNumber: null,
            sortOrder: 0,
          },
        ]
      : [];
  };

  /**
   * The Mix # for a colour on a job (2026-10-16 item 26), "" when unset --
   * the book then prints a blank, never the colour.
   */
  mixNumberFor = (projectId: string, color: string): string => {
    const wanted = color.trim().toLowerCase();
    if (!wanted) return "";
    return (
      this.colorsForProject(projectId)
        .find((c) => c.color.trim().toLowerCase() === wanted)
        ?.mixNumber?.trim() ?? ""
    );
  };

  /**
   * Set a colour's Mix #. A colour that exists only as the job's legacy single
   * colour has no row to hold it, so one is made for it here.
   */
  setColorMixNumber = async (projectId: string, colorRow: MMProjectColor, mixNumber: string): Promise<boolean> => {
    const value = mixNumber.trim() || null;
    if (colorRow.id) return this.updateProjectColor(colorRow.id, { mixNumber: value });
    if (!value) return true;
    return !!(await this.addProjectColor(projectId, colorRow.color, colorRow.mixDesignFileUrl, value));
  };

  /** True when the colour picker is worth showing at all. */
  hasMultipleColors = (projectId: string): boolean =>
    this.colorsForProject(projectId).length > 1;

  addProjectColor = async (
    projectId: string,
    color: string,
    mixDesignFileUrl?: string | null,
    mixNumber?: string | null
  ): Promise<MMProjectColor | null> => {
    const trimmed = color.trim();
    if (!trimmed) return null;

    // The DB's unique index is case-insensitive; refuse here too so he gets a
    // sentence instead of a 500.
    const clash = this.colorsForProject(projectId).some(
      (c) => c.color.trim().toLowerCase() === trimmed.toLowerCase() && c.id
    );
    if (clash) {
      this.rootStore.notificationStore?.addNotification({
        message: `${trimmed} is already on this job.`,
        options: { variant: "warning" },
      });
      return null;
    }

    try {
      const existing = this.projectColors.filter((c) => c.projectId === projectId);
      const created: any = await this.colorsService.create({
        projectId,
        color: trimmed,
        mixDesignFileUrl: mixDesignFileUrl ?? null,
        // Only when there is one: an API a deploy behind refuses the column.
        ...(mixNumber?.trim() ? { mixNumber: mixNumber.trim() } : {}),
        sortOrder: existing.reduce((max, c) => Math.max(max, c.sortOrder), -1) + 1,
      });
      const row: MMProjectColor = {
        id: created.projectColorId,
        projectId: created.projectId,
        color: created.color,
        mixDesignFileUrl: created.mixDesignFileUrl ?? null,
        mixNumber: created.mixNumber ?? null,
        sortOrder: Number(created.sortOrder) || 0,
      };
      runInAction(() => {
        this.projectColors.push(row);
      });
      return row;
    } catch (error) {
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to add the colour"),
        options: { variant: "error" },
      });
      return null;
    }
  };

  updateProjectColor = async (
    id: string,
    data: Partial<Pick<MMProjectColor, "color" | "mixDesignFileUrl" | "mixNumber" | "sortOrder">>
  ): Promise<boolean> => {
    if (!id) return false;
    try {
      const patched: any = await this.colorsService.patch(id, data);
      runInAction(() => {
        const index = this.projectColors.findIndex((c) => c.id === id);
        if (index !== -1) {
          this.projectColors[index] = {
            ...this.projectColors[index],
            color: patched.color ?? this.projectColors[index].color,
            mixDesignFileUrl: patched.mixDesignFileUrl ?? null,
            mixNumber: patched.mixNumber ?? null,
            sortOrder: Number(patched.sortOrder) || 0,
          };
        }
      });
      return true;
    } catch (error) {
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to update the colour"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  /**
   * Removing a colour from the job's list does NOT touch the ticket groups that
   * already pour it -- `mmTicketGroups.color` is a plain string for exactly this
   * reason. A group keeps what it poured; the colour just stops being offered.
   */
  deleteProjectColor = async (id: string): Promise<boolean> => {
    if (!id) return false;
    try {
      await this.colorsService.remove(id);
      runInAction(() => {
        this.projectColors = this.projectColors.filter((c) => c.id !== id);
      });
      return true;
    } catch (error) {
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to remove the colour"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  getProjectById = (id: string): MMProject | undefined => {
    return this.projects.find((p) => p.id === id);
  };

  // The jobs that ship as one truck: a project plus its paired half, if it has
  // one. Creede, 2026-08-26: "It's one shipment, and it can be both products."
  //
  // The single source of truth for "the pair" — every load-side read that has
  // to span both halves goes through this, so the halves cannot drift apart
  // one call site at a time.
  //
  // Always returns `projectId` first, and returns exactly `[projectId]` for an
  // unpaired job, so callers that index [0] for "the owning job" stay correct
  // and an unpaired job behaves exactly as it did before pairing existed.
  //
  // Self-pairing and a stale pairedProjectId both collapse to the single-job
  // case rather than producing a duplicate id: the pairing is stored as a plain
  // nullable column with no reciprocal trigger (see setPairedProject), so a
  // one-sided or dangling link is representable in the data.
  projectScopeFor = (projectId: string): string[] => {
    if (!projectId) return [];
    const pairedId = this.getProjectById(projectId)?.pairedProjectId;
    if (!pairedId || pairedId === projectId) return [projectId];
    // A link pointing at a project that is not loaded would silently widen
    // every scoped read to a job that cannot be resolved for billing.
    if (!this.getProjectById(pairedId)) return [projectId];
    return [projectId, pairedId];
  };

  // Case-insensitive because the server is: its collation treats "3980v" and
  // "3980V" as the same job, so a lookup here must not disagree.
  getProjectByJobNumber = (jobNumber: string): MMProject | undefined => {
    const target = jobNumber.trim().toUpperCase();
    return this.projects.find((p) => p.jobNumber.toUpperCase() === target);
  };

  getProjectsByCompany = (companyId: string): MMProject[] => {
    return this.projects.filter((p) => p.companyId === companyId);
  };

  // ========================================
  // ACTIONS
  // ========================================

  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };

  setFilterByStatus = (status: DetailingStatus | "all") => {
    this.filterByStatus = status;
  };

  setFilterByProductType = (type: ProductTypeFilter) => {
    this.filterByProductType = type;
  };

  setShowInactive = (show: boolean) => {
    this.showInactive = show;
  };

  setSelectedProjectId = (id: string | null) => {
    this.selectedProjectId = id;
  };

  calculateBillingRate = (
    contractAmount: number,
    designEngAllocation: number,
    mockupAllocation: number,
    totalCuFt: number
  ): number => {
    if (totalCuFt === 0) return 0;
    return (contractAmount - designEngAllocation - mockupAllocation) / totalCuFt;
  };

  // Both rates for one project: the estimate's and the tickets'. Returned
  // together because Creede wants them "both tracked and shown throughout" —
  // seeing the pair is how he judges whether adopting the actual is worth it.
  //
  // `actual` is null, never 0, when no actual cubic footage has been recorded:
  // a job with no tickets entered yet has no actual rate, and printing $0.00/cu.ft
  // for it would read as a priced job worth nothing.
  billingRates = (
    project: Pick<
      MMProject,
      | "contractAmount"
      | "designEngineeringAllocation"
      | "mockupAllocation"
      | "totalCubicFeet"
      | "actualCubicFeet"
    >
  ): { estimate: number; actual: number | null } => {
    const round = (value: number) => Math.round(value * 100) / 100;
    const estimate = round(
      this.calculateBillingRate(
        project.contractAmount,
        project.designEngineeringAllocation,
        project.mockupAllocation,
        project.totalCubicFeet
      )
    );
    const actual =
      project.actualCubicFeet == null || project.actualCubicFeet <= 0
        ? null
        : round(
            this.calculateBillingRate(
              project.contractAmount,
              project.designEngineeringAllocation,
              project.mockupAllocation,
              project.actualCubicFeet
            )
          );
    return { estimate, actual };
  };

  // The rate that governs invoicing: whichever basis was chosen.
  //
  // Falls back to the estimate when the basis says `actual` but no actual has
  // been recorded. That combination is reachable — pick the actual basis, then
  // clear the figure — and the alternative is a job that silently prices at
  // zero. The UI says which one is in force so the fallback is never invisible.
  effectiveBillingRate = (
    project: Parameters<MMProjectStore["billingRates"]>[0] & {
      billingRateBasis?: BillingRateBasis;
    }
  ): number => {
    const rates = this.billingRates(project);
    return project.billingRateBasis === "actual" && rates.actual != null
      ? rates.actual
      : rates.estimate;
  };

  // `scheduleColor` and `mixDesignFileUrl` are optional on the way in.
  //
  // Neither is the project form's to supply: the colour is written only by
  // ensureScheduleColor/releaseScheduleColor, and the mix-design URL only when
  // a file is actually attached. Requiring them here forced the form to send
  // both on every save, and a save naming a column the database has not got yet
  // fails outright -- which is how these two broke every project edit on an
  // instance whose migration had not run.
  createProject = async (
    data: Omit<
      MMProject,
      | "id"
      | "billingRatePerCuFt"
      | "readyForProduction"
      | "scheduleColor"
      | "mixDesignFileUrl"
    > & { scheduleColor?: string | null; mixDesignFileUrl?: string | null }
  ): Promise<MMProject> => {
    const billingRate = this.calculateBillingRate(
      data.contractAmount,
      data.designEngineeringAllocation,
      data.mockupAllocation,
      data.totalCubicFeet
    );

    const readyForProduction =
      data.hasShopAndField &&
      data.hasProductionTickets &&
      data.hasRequisitions &&
      data.hasErectionRequisitions &&
      data.hasShopAndFieldFinal;

    try {
      // Strip empty strings so optional fields don't fail validation
      const clean = (v: string | undefined) => (v && v.trim() ? v : undefined);

      const apiData = {
        // Upper-cased on the way out as well as in the form — see
        // MMProject.jobNumber for why a case split is not cosmetic.
        jobNumber: data.jobNumber.trim().toUpperCase(),
        jobName: data.jobName,
        jobSiteAddress: clean(data.jobSiteAddress),
        jobSiteCity: clean(data.jobSiteCity),
        jobSiteState: clean(data.jobSiteState),
        jobSiteZip: clean(data.jobSiteZip),
        companyId: data.companyId,
        billingContactId: clean(data.billingContactId),
        projectManagerId: clean(data.projectManagerId),
        jobSiteContactId: clean(data.jobSiteContactId),
        productType: data.productType,
        // ?? undefined — omit when unpaired (matches clean() above); null
        // would also be accepted by the API but there is nothing to clear
        // on a brand-new row.
        pairedProjectId: data.pairedProjectId ?? undefined,
        contractAmount: data.contractAmount,
        taxStatus: data.taxStatus,
        active: data.active ?? true,
        totalCubicFeet: data.totalCubicFeet,
        actualCubicFeet: data.actualCubicFeet ?? null,
        concreteColor: data.concreteColor ?? null,
        mixDesignFileUrl: data.mixDesignFileUrl ?? null,
        scheduleColor: data.scheduleColor ?? null,
        billingRateBasis: data.billingRateBasis ?? "estimate",
        designEngineeringAllocation: data.designEngineeringAllocation,
        mockupAllocation: data.mockupAllocation,
        // Sent only when entered: naming a column the database hasn't got yet
        // fails the whole create (see scheduleColor above).
        ...(data.estimatorForms != null ? { estimatorForms: data.estimatorForms } : {}),
        ...(data.estimatorFormHours != null ? { estimatorFormHours: data.estimatorFormHours } : {}),
        billingRatePerCuFt: Math.round(billingRate * 100) / 100,
        detailingStatus: data.detailingStatus,
        engineeringDetailer: clean(data.engineeringDetailer),
        erectionDetailer: clean(data.erectionDetailer),
        ticketDetailer: clean(data.ticketDetailer),
        dateBooked: clean(toDateOnly(data.dateBooked)),
        sentToDetailersDate: clean(toDateOnly(data.sentToDetailersDate)),
        submittalDate: clean(toDateOnly(data.submittalDate)),
        approvalDate: clean(toDateOnly(data.approvalDate)),
        holdDate: clean(toDateOnly(data.holdDate)),
        hasShopAndField: data.hasShopAndField,
        hasProductionTickets: data.hasProductionTickets,
        productionTicketsFinal: data.productionTicketsFinal,
        hasRequisitions: data.hasRequisitions,
        hasErectionRequisitions: data.hasErectionRequisitions,
        hasShopAndFieldFinal: data.hasShopAndFieldFinal,
        readyForProduction,
      };

      const result: any = await this.service.create(apiData);

      const newProject: MMProject = {
        ...data,
        id: result.projectId,
        // The row that exists is the normalized one, not what was typed.
        jobNumber: apiData.jobNumber,
        billingRatePerCuFt: Math.round(billingRate * 100) / 100,
        readyForProduction,
        // Optional on the way in (the form does not supply either), but the
        // local record is a full MMProject. A new job starts uncoloured and
        // with no mix design, which is what null means for both.
        scheduleColor: data.scheduleColor ?? null,
        mixDesignFileUrl: data.mixDesignFileUrl ?? null,
      };

      runInAction(() => {
        this.projects.push(newProject);
      });

      // "When a project is created, that'll be our date booked."
      await this.logStageEvent(
        newProject.id,
        null,
        newProject.detailingStatus,
        toDateOnly(newProject.dateBooked) || localToday()
      );

      // Point the sibling back at the new project. Symmetry is enforced
      // client-side — the backend stores pairedProjectId as a plain column
      // with no reciprocal trigger (see setPairedProject).
      if (data.pairedProjectId) {
        try {
          await this.service.patch(data.pairedProjectId, {
            pairedProjectId: newProject.id,
          });
          runInAction(() => {
            const sibling = this.projects.find(
              (p) => p.id === data.pairedProjectId
            );
            if (sibling) sibling.pairedProjectId = newProject.id;
          });
        } catch (error) {
          this.rootStore.notificationStore?.addNotification({
            message: mmErrorMessage(
              error,
              "Project created, but linking the paired job failed"
            ),
            options: { variant: "error" },
          });
          console.error("Failed to link paired job:", error);
        }
      }

      return newProject;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to create project";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to create project"),
        options: { variant: "error" },
      });
      throw error;
    }
  };

  updateProject = async (
    id: string,
    data: Partial<MMProject>
  ): Promise<boolean> => {
    const index = this.projects.findIndex((p) => p.id === id);
    if (index === -1) return false;

    const current = this.projects[index];
    const updated = { ...current, ...data };

    // Recalculate billing rate if relevant fields changed
    if (
      data.contractAmount !== undefined ||
      data.designEngineeringAllocation !== undefined ||
      data.mockupAllocation !== undefined ||
      data.totalCubicFeet !== undefined ||
      // Both of these move the rate now: the actual figure is a denominator
      // candidate, and the basis decides which denominator wins.
      data.actualCubicFeet !== undefined ||
      data.billingRateBasis !== undefined
    ) {
      updated.billingRatePerCuFt = this.effectiveBillingRate(updated);
    }

    // Recalculate readyForProduction
    updated.readyForProduction =
      updated.hasShopAndField &&
      updated.hasProductionTickets &&
      updated.hasRequisitions &&
      updated.hasErectionRequisitions &&
      updated.hasShopAndFieldFinal;

    try {
      // Build patch data excluding the local 'id' alias
      const { id: _id, ...rawPatch } = data;
      if (updated.billingRatePerCuFt !== current.billingRatePerCuFt) {
        (rawPatch as any).billingRatePerCuFt = updated.billingRatePerCuFt;
      }
      if (updated.readyForProduction !== current.readyForProduction) {
        (rawPatch as any).readyForProduction = updated.readyForProduction;
      }

      // Strip empty strings for optional UUID/string fields
      // The contact columns are NOT stripped any more: "" or null means "clear
      // it", and the API takes null since 2026-10-14. Stripping them was why a
      // contact could never be removed.
      const uuidFields = ['billingContactId', 'projectManagerId', 'jobSiteContactId'];
      for (const field of uuidFields) {
        if (field in rawPatch && ((rawPatch as any)[field] === '' || (rawPatch as any)[field] === undefined)) {
          (rawPatch as any)[field] = null;
        }
      }
      const optionalStringFields = [
        'engineeringDetailer', 'erectionDetailer', 'ticketDetailer',
        'dateBooked', 'sentToDetailersDate', 'submittalDate', 'approvalDate', 'holdDate',
        'jobSiteAddress', 'jobSiteCity', 'jobSiteState', 'jobSiteZip',
      ];
      const patchData: Record<string, unknown> = { ...rawPatch };
      optionalStringFields.forEach((field) => {
        if (field in patchData && (patchData[field] === '' || patchData[field] === null)) {
          delete patchData[field];
        }
      });

      // Normalize DATE fields to yyyy-MM-dd — the API rejects a full datetime
      // for `format: 'date'` columns, which would otherwise abort the edit.
      const dateFields = [
        'dateBooked', 'sentToDetailersDate', 'submittalDate', 'approvalDate', 'holdDate',
      ];
      dateFields.forEach((field) => {
        if (field in patchData && patchData[field] != null) {
          patchData[field] = toDateOnly(patchData[field]);
        }
      });

      if (typeof patchData.jobNumber === "string") {
        patchData.jobNumber = patchData.jobNumber.trim().toUpperCase();
      }

      await this.service.patch(id, patchData);

      runInAction(() => {
        this.projects[index] = updated;
      });

      // The move is logged only once the status itself has saved: a failed
      // patch logs nothing. Its date is the stage's date when this edit set
      // one (the Edit Pipeline Card dialog can), else today.
      if (updated.detailingStatus !== current.detailingStatus) {
        const dateField = STATUS_DATE_FIELD[updated.detailingStatus];
        const stamped = dateField ? toDateOnly(data[dateField]) : "";
        await this.logStageEvent(
          id,
          current.detailingStatus,
          updated.detailingStatus,
          stamped || localToday()
        );
      }

      // A rate change has to reach the loads. Nothing else re-prices them —
      // the load store only recalculates on a pallet change — so without this
      // every existing load keeps the old rate, and loads built before the
      // contract existed stay at $0 forever. Fire-and-forget: the patch above
      // has already succeeded, and a re-pricing failure toasts on its own.
      if (updated.billingRatePerCuFt !== current.billingRatePerCuFt) {
        void this.rootStore.mmLoadStore?.repriceLoadsForProject(id);
      }
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to update project";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to update project"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  // ========================================
  // PIPELINE LOG (10-16 items 6 and 9)
  // ========================================

  // Never throws: the move itself has already saved. A log that failed to
  // write says so, rather than leaving a gap nobody knows about.
  private logStageEvent = async (
    projectId: string,
    fromStatus: string | null,
    toStatus: string,
    occurredOn: string
  ): Promise<void> => {
    try {
      const created = (await this.stageEventsService.create({
        projectId,
        fromStatus,
        toStatus,
        occurredOn,
      })) as StageEventRow;
      runInAction(() => {
        this.projectStageEvents.push(toStageEvent(created));
      });
    } catch (error) {
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "The job moved, but its move was not logged"),
        options: { variant: "warning" },
      });
    }
  };

  // A job's moves, oldest first.
  stageEventsFor = (projectId: string): MMProjectStageEvent[] =>
    this.projectStageEvents
      .filter((e) => e.projectId === projectId)
      .sort(
        (a, b) => a.occurredOn.localeCompare(b.occurredOn) || a.createdAt.localeCompare(b.createdAt)
      );

  // The day a job entered the lane it is in now: the latest logged move into
  // that lane, else that stage's own date column, else the day it was booked.
  // Creede: "display the date that something moved to the section it's
  // currently in."
  enteredStatusOn = (project: MMProject): string | null => {
    const moves = this.stageEventsFor(project.id).filter(
      (e) => e.toStatus === project.detailingStatus
    );
    if (moves.length > 0) return moves[moves.length - 1].occurredOn;
    const dateField = STATUS_DATE_FIELD[project.detailingStatus];
    return (dateField && project[dateField]) || project.dateBooked || null;
  };

  // Corrections from the D&E tab. Only the date changes, or the move is
  // withdrawn: rewriting where a job went would falsify the log.
  updateStageEventDate = async (eventId: string, occurredOn: string): Promise<boolean> => {
    try {
      await this.stageEventsService.patch(eventId, { occurredOn });
      runInAction(() => {
        const event = this.projectStageEvents.find((e) => e.id === eventId);
        if (event) event.occurredOn = occurredOn;
      });
      return true;
    } catch (error) {
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to change the date"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  removeStageEvent = async (eventId: string): Promise<boolean> => {
    try {
      await this.stageEventsService.patch(eventId, { active: false });
      runInAction(() => {
        this.projectStageEvents = this.projectStageEvents.filter((e) => e.id !== eventId);
      });
      return true;
    } catch (error) {
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to remove the move"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  updateProjectStatus = async (
    id: string,
    status: DetailingStatus
  ): Promise<boolean> => {
    const project = this.getProjectById(id);
    if (!project) return false;

    const dateField = this.getDateFieldForStatus(status);
    const updates: Partial<MMProject> = { detailingStatus: status };

    if (dateField) {
      (updates as Record<string, unknown>)[dateField] = localToday();
    }

    return this.updateProject(id, updates);
  };

  private getDateFieldForStatus = (
    status: DetailingStatus
  ): keyof MMProject | null => {
    switch (status) {
      case "sent_to_detailers":
        return "sentToDetailersDate";
      case "out_for_approval":
        return "submittalDate";
      case "approved":
      case "approved_partial":
        return "approvalDate";
      case "on_hold":
        return "holdDate";
      default:
        return null;
    }
  };

  // Pair / unpair sibling VDT+precast jobs (item 12). The backend stores
  // pairedProjectId as a plain nullable column with NO reciprocal trigger, so
  // SYMMETRY IS ENFORCED CLIENT-SIDE HERE: pairing A<->B patches both rows
  // (A.pairedProjectId = B, B.pairedProjectId = A) and clearing (pairedId
  // null) breaks both sides. If either side was previously paired with some
  // third project, that stale link is cleared too, so no row is left pointing
  // at a job that no longer points back.
  setPairedProject = async (
    projectId: string,
    pairedId: string | null
  ): Promise<boolean> => {
    const project = this.getProjectById(projectId);
    if (!project) return false;

    const previousPairId = project.pairedProjectId ?? null;
    if (previousPairId === pairedId) return true; // no change

    // Deduped projectId -> new pairedProjectId value.
    const patches = new Map<string, string | null>();
    patches.set(projectId, pairedId);
    if (previousPairId) patches.set(previousPairId, null);
    if (pairedId) {
      const newSiblingPrevPair =
        this.getProjectById(pairedId)?.pairedProjectId ?? null;
      if (newSiblingPrevPair && newSiblingPrevPair !== projectId) {
        patches.set(newSiblingPrevPair, null);
      }
      patches.set(pairedId, projectId);
    }

    try {
      await Promise.all(
        Array.from(patches, ([id, value]) =>
          this.service.patch(id, { pairedProjectId: value })
        )
      );
      runInAction(() => {
        patches.forEach((value, id) => {
          const target = this.projects.find((p) => p.id === id);
          if (target) target.pairedProjectId = value;
        });
      });
      return true;
    } catch (error) {
      // Partial failure of the multi-patch can leave the server rows
      // asymmetric; the next loadData shows whatever the server has.
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to update paired job";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to update paired job"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  deleteProject = async (id: string): Promise<boolean> => {
    try {
      await this.service.remove(id);

      runInAction(() => {
        const index = this.projects.findIndex((p) => p.id === id);
        if (index !== -1) {
          this.projects.splice(index, 1);
        }
        if (this.selectedProjectId === id) {
          this.selectedProjectId = null;
        }
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to delete project";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to delete project"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  // ========================================
  // RESET
  // ========================================

  resetToDefaults = () => {
    runInAction(() => {
      this.projects = [];
      this.selectedProjectId = null;
      this.searchTerm = "";
      this.filterByStatus = "all";
      this.filterByProductType = "all";
      this.showInactive = true;
      this.error = null;
    });
    this.loadData();
  };
}

export default MMProjectStore;
